import { Box, Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useWindowSize } from 'react-use';
import Button from '~/components/Button';
import Modal from '~/components/Modal/Modal';
import { MOBILE_MIN_WIDTH } from '~/constants';

import DayPickerRangeControllerWrapper from './DayPickerRangeControllerWrapper';
import { DATE_FORMAT, END_DATE, START_DATE } from './utils';

const propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  onDatesChange: PropTypes.func.isRequired,
  controllerProps: PropTypes.object,
  onClear: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  focusedInput: PropTypes.string,
  onFocusedInputChange: PropTypes.func.isRequired,
  hasDateSet: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  mainButton: PropTypes.node.isRequired,
};

/* eslint-disable react/prop-types */
const SelectionButtons = ({ _isFocused, ...props }) => (
  <Button
    backgroundColor="white"
    fontSize="sm"
    textTransform="none"
    fontWeight="normal"
    flexGrow="1"
    textAlign="left"
    justifyContent="flex-start"
    width="50%"
    letterSpacing="0"
    // No focused state on mobile design
    // borderColor={isFocused ? 'primary.500' : 'gray.500'}
    {...props}
  />
);
/* eslint-enable react/prop-types */

const PreText = (props) => <Text color="gray.500" marginRight={2} {...props} />;

// default is 38
const getDaySize = (width) => {
  if (width > MOBILE_MIN_WIDTH) {
    return undefined;
  }
  if (width <= 350) {
    return undefined;
  }

  return Math.floor((width - 40) / 7);
};

const MobileDatePicker = ({
  startDate,
  endDate,
  onDatesChange: _onDatesChange,
  controllerProps,
  onClear,
  onApply,
  onDateChange,
  focusedInput,
  onFocusedInputChange,
  hasDateSet,
  isOpen,
  onOpen: _onOpen,
  onClose,
  mainButton,
}) => {
  const { width } = useWindowSize();

  const daySize = getDaySize(width);

  return (
    <>
      {mainButton}
      <Modal isOpen={isOpen} onClose={onClose}>
        <Modal.Header
          color="gray.500"
          fontSize="md"
          fontWeight="normal"
          paddingX={3}
          paddingBottom={2}
        >
          Select Date:
        </Modal.Header>
        <Modal.CloseButton />
        <Modal.Body padding={0} overflowY="auto">
          <Box height="420px">
            <DayPickerRangeControllerWrapper
              startDate={startDate}
              endDate={endDate}
              onDatesChange={onDateChange}
              focusedInput={focusedInput}
              onFocusChange={onFocusedInputChange}
              numberOfMonths={1}
              daySize={daySize}
              transitionDuration={0}
              {...controllerProps}
            />
          </Box>
          <Flex boxShadow="floating" position="relative">
            <SelectionButtons
              color={startDate ? 'secondary.500' : 'gray.400'}
              onClick={() => onFocusedInputChange(START_DATE)}
              aria-label="Select from date"
              isFocused={focusedInput === START_DATE}
            >
              <>
                <PreText>From</PreText>
                {startDate && startDate.format(DATE_FORMAT)}
              </>
            </SelectionButtons>
            <SelectionButtons
              color={endDate ? 'secondary.500' : 'gray.400'}
              onClick={() => onFocusedInputChange(END_DATE)}
              aria-label="Select to date"
              isFocused={focusedInput === END_DATE}
            >
              <>
                <PreText>To</PreText>
                {endDate && endDate.format(DATE_FORMAT)}
              </>
            </SelectionButtons>
          </Flex>
        </Modal.Body>
        <Modal.Footer
          alignItems="center"
          flexDirection="row-reverse"
          justifyContent="space-between"
          spaceBetween
          paddingX={3}
          paddingY={2}
        >
          <Button
            onClick={() => {
              onApply();
              onClose();
            }}
            colorScheme="primary"
            aria-label="Apply date selection"
          >
            Apply
          </Button>
          {hasDateSet ? (
            <Button
              onClick={() => {
                onClear();
                onClose();
              }}
              variant="ghost"
              textTransform="none"
              letterSpacing="0"
            >
              Clear Selection
            </Button>
          ) : (
            <Button
              onClick={() => {
                onClose();
              }}
              variant="ghost"
              textTransform="none"
              letterSpacing="0"
            >
              Cancel
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

MobileDatePicker.propTypes = propTypes;

export default MobileDatePicker;
